<template>
  <div class="summary">
    <ValidationProvider
      v-slot="{ errors, value, handleChange }"
      v-model="summaryText"
      :name="titleList[post.topicType]"
      :rules="rules"
    >
      <div class="formLabelWrapper">
        <KkTooltip
          class="summary__tooltip"
          :label="`${titleList[post.topicType]}(${$t('labels.countCharacters', { count: value ? value.length : 0, max: maxLength })})${ requiredOrOptionalLabelText }`"
          top="25px"
          width="400px"
          left="0"
          font-size="12px"
          font-weight="bold"
          :is-only-text="!tooltipText.length"
          :is-visible="showTooltip"
          @showTooltip="showTooltip = true"
          @hideTooltip="showTooltip = false"
        >
          <p class="summary__tooltip__text" v-html="tooltipText" />
        </KkTooltip>
        <div class="formLabelWrapper__buttons">
          <KkInsertParameter
            class="insertParameter"
            :parameters="availableParameters"
            :loading="parameterLoading"
            @insert-parameter="insertParam"
          />
          <button v-if="providedFeaturesAiPostSupport" class="formLabelWrapper__ai" @click="openModal">
            <KkIcon name="pencilUnderLine" color="#E9446A" class="formLabelWrapper__aiIcon" />
            {{ $t('pages.CompaniesStorePost.aiPost.title') }}
          </button>
        </div>
      </div>
      <KkAiPostModal v-model:is-visible="modal" @submit="handleSubmit" />
      <label>
        <textarea
          ref="summary"
          :value="value"
          class="c-textArea"
          :maxlength="maxLength"
          rows="10"
          @input="handleChange"
        />
      </label>

      <ValidationError id="summary" :error="errors[0]" />
    </ValidationProvider>
    <div class="infoContainer">
      <div class="infoParameter">
        <KkInfo>
          {{ $t('pages.CompaniesStorePost.summaryParameter', { parameterName: "{AREA_NAME}" }) }}
        </KkInfo>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { KkTooltip, KkIcon } from '../../components/base'
import { KkInfo } from '../ui'
import ValidationError from '../ValidationError'
import { KkInsertParameter } from '../../components/projects'
import KkAiPostModal from '../../domains/post/components/KkAiPostModal/index.vue'

export default {
  name: 'Summary',
  components: {
    KkTooltip,
    KkIcon,
    KkInfo,
    ValidationError,
    KkInsertParameter,
    KkAiPostModal,
  },
  props: {
    availableParameters: {
      type: Array,
      default: () => []
    },
    parameterLoading: {
      type: Boolean,
      required: false
    },
  },
  data() {
    return {
      showTooltip: false,
      modal: false,
    }
  },
  computed: {
    ...mapState('companiesStorePosts', ['post']),
    ...mapGetters({
      providedFeaturesAiPostSupport: 'companiesCompany/providedFeaturesAiPostSupport',
      useServiceName: 'companiesCompany/useServiceName'
    }),
    titleList() {
      return {
        STANDARD: this.$t('pages.CompaniesStorePost.summary.title.standard'),
        EVENT: this.$t('pages.CompaniesStorePost.summary.title.event'),
        OFFER: this.$t('pages.CompaniesStorePost.summary.title.offer'),
      }
    },
    summaryText: {
      get() {
        return this.post.summary
      },
      set(summary) {
        return this.updateSummary(summary)
      },
    },
    // 選択サービスの上限が最も大きいものに合わせる（上限を超えたものに関しては…で省略）
    maxLength() {
      // googleが含まれている場合は1500文字まで
      if (this.includesGoogle) return 1500

      // yahooMapが含まれている場合は330文字まで
      if (this.includesYahooMap) return 330

      // docomoだけの場合は60文字まで
      return 60
    },
    rules() {
      let rules = `max:${this.maxLength}`
      if (this.includesYahooMap) {
        // requiredを最初にしたいのでこの書き方にした
        rules = `required|min:10|${rules}`
      }
      return rules
    },
    isRequired() {
      return this.rules.includes('required')
    },
    includesGoogle() {
      return this.post.services.includes('google_map')
    },
    includesYahooMap() {
      return this.post.services.includes('yahoo_map')
    },
    includesDocomo() {
      return this.post.services.includes('docomo')
    },
    tooltipText() {
      let text = `${this.$t('pages.CompaniesStorePost.summary.tooltip.baseText')}\n\n`

      if (this.includesGoogle) {
        text += `<strong>${this.$t('pages.CompaniesStorePost.service.googleMap')}</strong>\n`
        text += `${this.$t('pages.CompaniesStorePost.summary.tooltip.google')}\n\n`
      }
      if (this.includesDocomo) {
        text += `<strong>${this.$t('pages.CompaniesStorePost.service.docomo')}</strong>\n`
        text += `${this.$t('pages.CompaniesStorePost.summary.tooltip.docomo')}\n\n`
      }
      if (this.includesYahooMap) {
        text += `<strong>${this.$t('pages.CompaniesStorePost.service.yahooMap')}</strong>\n`
        text += `${this.$t('pages.CompaniesStorePost.summary.tooltip.yahooMap')}`
      }

      return text
    },
    isOnlyGoogle() {
      return this.post.services.length === 1 && this.post.services.includes('google_map')
    },
    showRequiredOrOptionalLabel() {
      // 投稿タイプが最新情報でgoogleのみ選択している場合は、必須・任意ラベルを表示しない
      if (this.post.topicType === 'STANDARD' && this.isOnlyGoogle) return false

      return true
    },
    requiredOrOptionalLabelText() {
      if (!this.showRequiredOrOptionalLabel) return ''
      return this.isRequired ? this.$t('labels.requiredLabel') : this.$t('labels.optionalLabel')
    },
  },
  methods: {
    ...mapMutations('companiesStorePosts', ['updateSummary']),
    insertParam (parameterName) {
      const pos = this.$refs.summary.selectionStart
      const before = this.summaryText.substring(0, pos)
      const after = this.summaryText.substring(pos, this.summaryText.length)
      this.summaryText = before + parameterName + after
    },
    handleCloseModal() {
      this.modal = false
    },
    handleSubmit(text) {
      this.modal = false
      this.post.summary = text
    },
    openModal() {
      this.modal = true
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../stylesheets/biz_spa/foundation/mixin";
.summary {
  &__tooltip {
    font-size: 12px;
    &__text {
      white-space: pre-wrap;
    }
  }
}
.formLabelWrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  &__buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0 20px;
  }
  &__ai {
    display: flex;
    align-items: center;
    color: #E9446A;
    font-size: 12px;
    text-align: left;
    gap: 0 8px;
  }
  &__aiIcon {
    width: 16px;
  }
  @include mq(xl) {
    flex-direction: row;
  }
}
.insertParameter {
  margin-bottom: 0;
}
.infoContainer {
  margin-top: 16px;
}
.label {
  margin-bottom: 8px;
  font-size: 12px;
}
</style>
